import styled, { css, keyframes } from 'styled-components';

import { ANIM, REDUCED_MOTION, STORYBOARD_FILMSTRIP } from '@/nova/theme/tokens';

/* The run preview's filmstrip: four cells per beat over the dot field, side by side with soft cross-faded seams, filled
   by the beat's drawn stills as soon as they exist and by its clip's frames once it renders; the cells land one at a
   time, left to right (see BeatFilmstrip). */

const revealIn = keyframes`
  from { clip-path: inset(0 100% 0 0); opacity: 0; }
  to { clip-path: inset(0 0 0 0); opacity: 1; }
`;

/** Fills the preview box under its hint copy (which sits at z-index 1). Its mask (the solid part and the drifting
 *  cloud edge) is written every frame by BeatFilmstrip's edge loop, so it is not styled here. */
export const Strip = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  opacity: ${STORYBOARD_FILMSTRIP.OPACITY};
  mask-repeat: no-repeat, repeat-y, repeat-y;
  -webkit-mask-repeat: no-repeat, repeat-y, repeat-y;
`;

/** One cell of the strip, placed and masked inline; hidden until its turn, then wipes in. It holds the beat's drawn
 *  still (shown as soon as the frame is drawn) and, once the beat's clip renders, the clip's frame over it. */
export const Cell = styled.div<{ $shown: boolean }>`
  position: absolute;
  top: 0;
  height: 100%;
  opacity: 0;

  ${({ $shown }) =>
    $shown &&
    css`
      opacity: 1;
      animation: ${revealIn} ${STORYBOARD_FILMSTRIP.FRAME_REVEAL_MS}ms ${ANIM.EASE} both;

      ${REDUCED_MOTION} {
        animation: none;
      }
    `}
`;

/** The beat's drawn frame (its start or end still), cover-fitted across its half of the beat's cells (its width and
 *  offset are set inline, so each cell shows its own slice of one continuous picture). */
export const Still = styled.img`
  position: absolute;
  top: 0;
  height: 100%;
  max-width: none;
  object-fit: cover;
`;

/** The clip's frame (a canvas, cover-fitted), cross-fading in over the still once painted. */
export const ClipFrame = styled.canvas<{ $painted: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${({ $painted }) => ($painted ? 1 : 0)};
  transition: opacity ${STORYBOARD_FILMSTRIP.CLIP_FADE_MS}ms ${ANIM.EASE};

  ${REDUCED_MOTION} {
    transition: none;
  }
`;
